// Click-and-drag scrolling for the `.horizontal-scroll` rails.
//
// Mouse and pen only. Touch already scrolls these natively, with momentum and
// rubber-banding this could not reproduce, so touch pointers are left alone.
//
// The rails hide their scrollbar at every viewport, which leaves a mouse with no
// way at all to reach the overflow: a wheel scrolls the block axis, and
// drag-to-scroll is a touch behaviour no browser emulates for a pointer. This is
// the affordance that replaces the bar. It amends ADR 0030's "zero JavaScript
// ships for scrolling" — the rest of that decision (CSS grid, peek as the
// at-rest cue, no arrows or dots) is unchanged.
import { onDocumentReady } from "@theme/utilities";

// Below this, a pointer sequence is a click that wobbled, not a drag. Capture
// and scroll writes wait for it too. Taking the pointer on pointerdown
// retargets the click to the rail, so a tile link in an overflowing rail
// never runs.
const DRAG_THRESHOLD_PX = 5;

// Fallback for ending the settle. `scrollend` is the real signal, but it never
// fires when the smooth scroll is pre-empted — a second drag started mid-glide,
// or a target the rail was already parked on — and leaving `--settling` behind
// would leave that rail unsnapped for the rest of the page's life.
const SETTLE_TIMEOUT_MS = 700;

function attach(port) {
  if (port.dataset.horizontalScrollDrag === "on") return;
  port.dataset.horizontalScrollDrag = "on";

  let pointerId = null;
  let startClientX = 0;
  let startScrollLeft = 0;
  let travelled = 0;
  // Owns the document listeners for the press in progress; aborting it drops
  // them all at once.
  let gesture = null;

  // Overflow is measured on the pointer, never at startup. ADR 0024 renders all
  // three Solutions panels in the DOM and hides two of them, so a rail in there
  // has zero width until its tab is shown — and an element you cannot point at
  // is one whose measurements do not matter yet.
  const overflows = () => port.scrollWidth > port.clientWidth;

  port.addEventListener("pointerenter", (event) => {
    if (event.pointerType === "touch") return;
    port.classList.toggle("horizontal-scroll--draggable", overflows());
  });

  port.addEventListener("pointerdown", (event) => {
    if (event.pointerType === "touch" || event.button !== 0) return;

    // Cleared before the overflow guard, not after it: a rail that has stopped
    // overflowing since the last drag — the window was widened — must not hold a
    // stale distance that swallows the next honest click.
    travelled = 0;
    if (!overflows()) return;

    pointerId = event.pointerId;
    startClientX = event.clientX;
    startScrollLeft = port.scrollLeft;

    port.classList.add("horizontal-scroll--dragging");

    // Grabbing the rail mid-glide takes it over. Ordered after `--dragging` so
    // snap is still suspended by the time `--settling` is dropped, and the
    // pointermove writes below cancel the smooth scroll still in flight.
    endSettle();

    // The rail holds no capture until the threshold, so a press that slips off
    // it first would send its moves and its release somewhere else, and
    // `--dragging` would stay on with snap suspended. The document hears the
    // whole press wherever the pointer goes.
    gesture?.abort();
    gesture = new AbortController();
    const { signal } = gesture;
    document.addEventListener("pointermove", onPointerMove, { signal });
    document.addEventListener("pointerup", endDrag, { signal });
    document.addEventListener("pointercancel", endDrag, { signal });
  });

  const onPointerMove = (event) => {
    if (event.pointerId !== pointerId) return;

    const delta = event.clientX - startClientX;
    travelled = Math.max(travelled, Math.abs(delta));
    if (travelled <= DRAG_THRESHOLD_PX) return;

    // Once it is a drag, the rail takes the pointer, so nothing the cursor
    // passes over on the way reacts to it.
    if (!port.hasPointerCapture(pointerId)) port.setPointerCapture(pointerId);
    port.scrollLeft = startScrollLeft - delta;
  };

  let settleTimer = null;
  let settleTarget = null;

  // Where the browser's own snap would land: the item whose inline-start edge is
  // nearest the current scroll offset, which is what `scroll-snap-align: start`
  // means. Measured through `getBoundingClientRect` rather than `offsetLeft`,
  // because the items' offset parent is not the port.
  const nearestSnapOffset = () => {
    const portLeft = port.getBoundingClientRect().left;
    // Snap aligns to the snapport, which `scroll-padding` insets, so a rail
    // that pads it rests its cards on the page margin, not the screen edge.
    const scrollPadding =
      Number.parseFloat(getComputedStyle(port).scrollPaddingInlineStart) || 0;
    // A snap point past either end of the track is unreachable, and the browser
    // clamps it to that end before choosing the nearest. Clamping first does the
    // same, and asking for an unclamped one would leave the rail short of the
    // target and `scrollend` never matching.
    const limit = port.scrollWidth - port.clientWidth;
    let best = null;
    let bestDistance = Infinity;
    for (const item of port.children) {
      // Snap points are read from `scroll-snap-align` itself rather than from
      // `.horizontal-scroll__item`. The class is only one way the declaration
      // arrives: `blocks/solutions-tabs.liquid:397` sets it structurally on the
      // port's direct children instead, because a product card has no
      // `custom_classes` setting and ADR 0022 forbids wrapping it in a group.
      // Testing the class here excluded that rail and settled it on a position
      // the browser then corrected — two motions, worse than the one replaced.
      //
      // Zero-width children are skipped: they are the `<style>` each block
      // emits, and a rail inside a hidden Solutions panel (ADR 0024) has no laid
      // out children at all. Neither can be snapped to.
      if (item.getBoundingClientRect().width === 0) continue;
      const align = getComputedStyle(item).scrollSnapAlign;
      if (align.split(/\s+/).every((axis) => axis === "none")) continue;
      const offset = Math.max(
        0,
        Math.min(
          item.getBoundingClientRect().left -
            portLeft +
            port.scrollLeft -
            scrollPadding,
          limit,
        ),
      );
      const distance = Math.abs(offset - port.scrollLeft);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = offset;
      }
    }
    // No declared snap points, so there is no position to settle on: leave the
    // browser to it rather than inventing a destination.
    if (best === null) return null;
    return Math.round(best);
  };

  const endSettle = () => {
    clearTimeout(settleTimer);
    settleTimer = null;
    settleTarget = null;
    port.removeEventListener("scrollend", onSettleScrollEnd);
    port.classList.remove("horizontal-scroll--settling");
  };

  // The drag's own `scrollLeft` writes stop when the pointer does, so the
  // browser has a `scrollend` of its own queued for about the moment the pointer
  // lifts. Taking that one at face value dropped `--settling` on the frame snap
  // was still suspended for, and the rail teleported exactly as before — the
  // whole fix looked inert. Only a `scrollend` with the rail actually parked on
  // the target ends the settle; anything earlier is the drag's, not ours.
  const onSettleScrollEnd = () => {
    if (settleTarget === null) return;
    if (Math.abs(port.scrollLeft - settleTarget) > 2) return;
    endSettle();
  };

  // Whether this rail snaps at all. Read from the custom property and not from
  // the computed `scroll-snap-type`, which is `none` for as long as `--dragging`
  // is on — reading the suspended value is what made the first cut of this a
  // silent no-op. An unset property means the primitive's own default, `x
  // mandatory`, so absent is snapping.
  const snaps = () => {
    const declared = getComputedStyle(port)
      .getPropertyValue("--horizontal-scroll-snap")
      .trim();
    return declared === "" || declared !== "none";
  };

  // Returns whether it took ownership of restoring snap.
  const beginSettle = () => {
    // A rail with snap off (Shop by Category, ADR 0046) has no card to settle on
    // and must stay exactly where the drag left it.
    if (!snaps()) return false;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches)
      return false;

    const target = nearestSnapOffset();
    if (target === null || Math.abs(target - port.scrollLeft) < 1) return false;

    settleTarget = target;
    port.classList.add("horizontal-scroll--settling");
    port.addEventListener("scrollend", onSettleScrollEnd);
    settleTimer = setTimeout(endSettle, SETTLE_TIMEOUT_MS);
    port.scrollTo({ left: target, behavior: "smooth" });
    return true;
  };

  const endDrag = (event) => {
    if (event.pointerId !== pointerId) return;

    gesture.abort();
    gesture = null;
    if (port.hasPointerCapture(pointerId))
      port.releasePointerCapture(pointerId);
    pointerId = null;

    // `--settling` has to go on before `--dragging` comes off. Both suspend
    // snap, and a single frame with neither is all the browser needs to
    // reposition the rail instantly, which is the teleport this replaces.
    const settling = beginSettle();
    port.classList.remove("horizontal-scroll--dragging");
    if (!settling) endSettle();
  };

  // Every tile is a link, so a drag that happens to finish over one would
  // navigate. Capture phase, to get in before the anchor's own handling.
  // `travelled` resets on each pointerdown, so an ordinary click is never
  // swallowed by a drag that came before it.
  port.addEventListener(
    "click",
    (event) => {
      // `detail === 0` is a click with no pointer behind it: Enter or Space on a
      // focused tile, or a programmatic `.click()`. A drag leaves `travelled`
      // above the threshold until the next pointerdown, and a keyboard
      // activation never issues one — so without this guard, dragging a rail
      // once disabled Enter on every tile in it for the rest of the page's life.
      if (event.detail === 0) return;
      if (travelled <= DRAG_THRESHOLD_PX) return;
      // Consumed, not left standing. One drag suppresses exactly the one click
      // it generated; anything after it is a fresh gesture and gets through on
      // its own merits.
      travelled = 0;
      event.preventDefault();
      event.stopPropagation();
    },
    { capture: true },
  );

  // A native link or image drag would pre-empt the pointer sequence and hand the
  // user a drag ghost instead of a scroll.
  port.addEventListener("dragstart", (event) => event.preventDefault());
}

onDocumentReady(() => {
  document
    .querySelectorAll(".horizontal-scroll > .group-block-content")
    .forEach(attach);
});
